import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	convertSortDirectionToDto,
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import {
	CommunityGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchCommunities,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	getPropertySortText,
	NoResults,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	SelectOption
} from "client/Components";
import { checkHasFilter } from "./common";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	RemoteDataSourceState,
	useValueEqualMemo
} from "client/Hooks";
import {
	isNil,
	isNilOrWhiteSpace
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { SortDirection } from "client/Api/common";
import { SearchNoResults } from "../SearchNoResults";
import { CommunityItemView } from "../Items/CommunityItemView";
import { Guid } from "client_legacy/ndaw/guid";

export type CommunitySearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const CommunitySearch = (props: CommunitySearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [formData, setFormData] = useState<CommunitySearchFormData>({
		...defaultFilters,
		sort: defaultSortValue
	});

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const listHandle: RefObject<ScrollableVirtualListHandle<CommunityGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<CommunityGlobalSearchResultItem>>(null);

	const formHandler: IFormHandler<CommunitySearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				isMember: formData.isMember
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			searchTerm,
			formData.isMember,
			formData.sort
		]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={65}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="community-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: CommunityGlobalSearchResultItem, index: number): ReactElement => (
						<CommunityItemView
							key={item.id}
							navigationIndex={index}
							item={item}
							itemScope={communitySearchScope}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
						isExpandedByDefault
					/>

					<SearchSettings.Select
						label={localize("_Membership")}
						attachable={formHandler.for("isMember")}
						options={membershipOptions}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
CommunitySearch.displayName = "CommunitySearch";

const PAGE_SIZE = 20;

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<CommunityGlobalSearchResultItem> & (
		| "name"
	);
};

type CommunitySearchFormData = {
	sort: SortValue;
	isMember: boolean | null;
};

type FilterModel = Omit<CommunitySearchFormData, "sort">;

const defaultFilters: FilterModel = {
	isMember: null
};

const defaultSortValue: SortValue = {
	propertyName: "name",
	direction: DataSourceSortDirection.Ascending
};

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: defaultSortValue,
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	}
];

const membershipOptions: readonly SelectOption<boolean | null>[] = [
	{
		text: localize("_All"),
		value: defaultFilters.isMember,
		valueTrackingId: "all"
	},
	{
		text: localize("_Joined"),
		value: true,
		valueTrackingId: "joined"
	},
	{
		text: localize("_NotJoined"),
		value: false,
		valueTrackingId: "not-joined"
	}
];

const useDataSource = createRemoteDataSource<
	CommunityGlobalSearchResultItem,
	FilterModel,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook({
		performRequest: async (options): Promise<LazyLoadPageResult<CommunityGlobalSearchResultItem>> => {
			const {
				searchTerm,
				after,
				amount,
				filterState,
				sorting,
				signal
			} = options;

			if (isNilOrWhiteSpace(searchTerm)) {
				return Promise.resolve({ items: [], hasMore: false });
			}

			return await searchCommunities(
				{
					searchTerm,
					amount,
					pagingInfo: after
						? {
							isSet: true,
							value: {
								name: after.name
							}
						}
						: { isSet: false },
					sortDirection: convertSort(sorting),
					shouldBeMember: isNil(filterState.isMember)
						? { isSet: false }
						: {
							isSet: true,
							value: filterState.isMember
						}
				},
				signal
			);
		}
	});

function convertSort(
	sortingState: DataSourceSortableState<CommunityGlobalSearchResultItem> | undefined
): SortDirection {
	if (!sortingState) {
		return SortDirection.Ascending;
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	switch (sortingState.propertyName) {
		case "name":
			return convertSortDirectionToDto(sortingState.direction);
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}
}

function getUniqueKey(item: CommunityGlobalSearchResultItem): string {
	return `${item.id}`;
}

const communitySearchScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Communities
};
